<template>
  <div class="doctor-image-container">
    <div class="doctor-image">
      <img
        :src="displayedProfileImage || require('@/assets/img/doctor-james.png')"
        :key="displayedProfileImage"
        alt="Doctor Profile"
      />
      <div v-if="isUploadingImage" class="loader"></div>
      <label v-if="!isUploadingImage" class="upload-icon" for="fileInput">
        <i class="fas fa-camera"></i>
      </label>
      <input
        type="file"
        id="fileInput"
        ref="profileImageInput"
        @change="handleImageUpload"
        accept="image/jpeg,image/png,image/webp"
        hidden
      />
    </div>
    <div v-if="selectedProfileImage && !isUploadingImage" class="upload-actions">
      <button class="btn-save" @click="saveProfilePicture">
        <i class="fa-solid fa-check"></i>
      </button>
      <button class="btn-cancel" @click="cancelImageUpload">
        <i class="fa-solid fa-times"></i>
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: "DoctorImageSection",
  props: {
    doctor: {
      type: Object,
      required: true,
    },
  },
  emits: ["update:doctor", "show-toast"],
  data() {
    return {
      selectedProfileImage: null,
      previewImage: null,
      isUploadingImage: false,
    };
  },
  computed: {
    displayedProfileImage() {
      // Priority to preview (when user selects new image)
      if (this.previewImage) {
        return this.previewImage;
      }
      // Otherwise show current doctor photo
      return this.doctor?.photo || null;
    },
  },
  methods: {
    handleImageUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      // Allowed image types
      const allowedTypes = ["image/jpeg", "image/png", "image/webp"];

      if (!allowedTypes.includes(file.type)) {
        this.$emit("show-toast", {
          type: "error",
          message: "Only JPG, PNG, or WebP images are allowed",
        });

        // Reset file input
        event.target.value = "";
        this.selectedProfileImage = null;
        this.previewImage = null;
        return;
      }

      // Max size 1MB
      const maxSize = 1 * 1024 * 1024;

      if (file.size > maxSize) {
        this.$emit("show-toast", {
          type: "error",
          message: "Image size must be less than 1MB",
        });

        // Reset file input
        event.target.value = "";
        this.selectedProfileImage = null;
        this.previewImage = null;
        return;
      }

      // Valid image → generate preview
      this.selectedProfileImage = file;
      this.previewImage = URL.createObjectURL(file);
    },
    
    async uploadProfileImage() {
      if (!this.selectedProfileImage) return null;

      try {
        const formData = new FormData();
        formData.append("file", this.selectedProfileImage);

        const baseUrl = process.env.VUE_APP_API_URL?.replace(/\/api\/?$/, "");
        if(!baseUrl) return;
        const response = await fetch(
          // "https://dev.mamabots.com/utility/upload",
          `${baseUrl}/utility/upload`,
          {
            method: "POST",
            body: formData,
          }
        );

        const data = await response.json();

        const uploadedImageUrl = data?.data?.signedReadUrl;

        if (!response.ok || !uploadedImageUrl) {
          this.selectedProfileImage = null;
          this.previewImage = null;
          throw new Error("Couldn't upload the image. Try again.");
        }

        return uploadedImageUrl;
      } catch (err) {
        console.error("Profile image upload error:", err);

        this.selectedProfileImage = null;
        this.previewImage = null;

        this.$emit("show-toast", {
          type: "error",
          message: "Couldn't upload the image. Try again.",
        });
        return null;
      }
    },
    
    async saveProfilePicture() {
      if (!this.selectedProfileImage) {
        this.$emit("show-toast", {
          type: "warning",
          message: "Please select an image first.",
        });
        return;
      }
      
      this.isUploadingImage = true;
      
      try {
        // Upload image to cloud storage
        const uploadedUrl = await this.uploadProfileImage();
        
        if (!uploadedUrl) {
          this.isUploadingImage = false;
          return;
        }
        
        // Update doctor object with new photo URL
        this.$emit('update:doctor', {
          ...this.doctor,
          photo: uploadedUrl
        });
        
        // Wait for next tick to ensure doctor object is updated
        await this.$nextTick();
        
        this.$emit("show-toast", {
          type: "success",
          message: "Profile picture updated successfully!",
        });
        
        // Now clear the selected image and preview
        this.selectedProfileImage = null;
        this.previewImage = null;
        
        // Reset file input
        if (this.$refs.profileImageInput) {
          this.$refs.profileImageInput.value = "";
        }
      } catch (error) {
        console.error('Error saving profile picture:', error);
        this.$emit("show-toast", {
          type: "error",
          message: "Failed to save profile picture.",
        });
      } finally {
        this.isUploadingImage = false;
      }
    },
    
    cancelImageUpload() {
      this.selectedProfileImage = null;
      this.previewImage = null;
      
      // Reset file input
      if (this.$refs.profileImageInput) {
        this.$refs.profileImageInput.value = "";
      }
    },
  },
};
</script>

<style scoped>
.doctor-image-container {
  text-align: center;
  margin-bottom: 20px;
}

.doctor-image {
  position: relative;
  display: inline-block;
}

.doctor-image img {
  width: 150px;
  height: 150px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid #e0e0e0;
  transition: all 0.3s ease;
}

.upload-icon {
  position: absolute;
  bottom: 10px;
  right: 10px;
  background: #3c78f9;
  color: white;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border: 2px solid white;
  transition: all 0.3s ease;
}

.upload-icon:hover {
  background: #2d5fcc;
  transform: scale(1.1);
}

.upload-icon i {
  font-size: 20px !important;
}

.loader {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3c78f9;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  z-index: 10;
}

.upload-actions {
  display: flex;
  gap: 0.75rem;
  justify-content: center;
  margin-top: 1rem;
}

.upload-actions button {
  padding: 0.5rem 1rem;
  border-radius: 8px;
  border: none;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-save {
  background: #28a745;
  color: white;
}

.btn-save:hover {
  background: #218838;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(40, 167, 69, 0.3);
}

.btn-cancel {
  background: #6c757d;
  color: white;
}

.btn-cancel:hover {
  background: #5a6268;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(108, 117, 125, 0.3);
}

@keyframes spin {
  0% {
    transform: translate(-50%, -50%) rotate(0deg);
  }
  100% {
    transform: translate(-50%, -50%) rotate(360deg);
  }
}
</style>